import { Button } from '@mm/components';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { nextBillingTypeForFeatureSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectCurrentUserIsAdmin } from '@mm/state/selectors/userSelector';
import { StoreState } from '@mm/state/store-interfaces';
import React from 'react';
import { useSelector } from 'react-redux';
import { PlanCircle } from '../../../../../../common/components/ProBadge';
import { UserRoleContainer } from '../../../../../../common/hooks/use-route-guard';
import { ExportContacts } from '../ExportContacts/ExportContacts';
import { useAudienceViewHandlers } from '../use-audience-view-handlers';
import styles from './Audience.module.css';
import { selectIsShopifyContext } from '@mm/state/selectors/viewSelectors';

export const AudienceToolbarButtons = ({
  audience,
  processClick,
  contactsCount,
}: {
  audience: AudiencesModel | null;
  contactsCount: number;
  processClick: (onSuccess: (args: any) => void) => void;
}) => {
  const { openAddAudienceToAutomationModal, closeModal } = useModalActions();
  const { onSync, onAllowContactsDeleteToggle } = useAudienceViewHandlers();
  const {
    roleFlags: { isMonitorLeadsRole },
  } = UserRoleContainer.useContainer();
  const isShopify = useSelector(selectIsShopifyContext);
  const isSysAdmin = useSelector(selectCurrentUserIsAdmin);
  const syncContactsBillingType = useSelector((state: StoreState) =>
    nextBillingTypeForFeatureSelector(state, 'audiencesSyncContacts'),
  );

  if (!audience) return <></>;

  const currentAudienceId = audience?.id;
  const audienceName = audience?.name ?? '';

  const onSyncClick = () => onSync(audience.id, audienceName);

  const shouldShowAddToAutomationBtn =
    !isShopify &&
    ((!isMonitorLeadsRole && audience && !audience?.additional) ||
      (audience?.additional &&
        ['csv'].includes(audience?.additional?.kind) &&
        !audience?.hidden));

  const onAddToAutomation = () => {
    openAddAudienceToAutomationModal({
      activeAudience: audience,
      closeModal,
    });
  };

  return (
    <div className={styles.toolbarContainer}>
      {shouldShowAddToAutomationBtn && (
        <Button
          color="secondary-blue"
          css={{ container: styles.button }}
          onClick={() => processClick(() => onAddToAutomation())}
        >
          Add to Automation
        </Button>
      )}
      <ExportContacts
        className={styles.button}
        color="secondary-blue"
        audienceId={currentAudienceId}
        disabled={!contactsCount}
        contactsCount={contactsCount}
        processClick={processClick}
      />
      {!isShopify && (
        <Button
          css={{
            container: styles.button,
          }}
          color="secondary-blue"
          onClick={() => processClick(() => onSyncClick())}
          disabled={!currentAudienceId}
        >
          Sync to Facebook
          {syncContactsBillingType && (
            <PlanCircle
              className={styles.circle}
              billingType={syncContactsBillingType}
              size={9}
            />
          )}
        </Button>
      )}
      {audience && isSysAdmin && (
        <Button
          color={audience.deleteContacts ? 'inverted-red' : 'secondary-blue'}
          onClick={() =>
            onAllowContactsDeleteToggle(audience.id, !audience.deleteContacts)
          }
        >
          {audience.deleteContacts
            ? 'Bulk Contacts Deletion Active'
            : 'Bulk Contacts Deletion'}
        </Button>
      )}
    </div>
  );
};
